import {
  Bell,
  BookOpen,
  Bookmark,
  Compass,
  Home,
  List as ListIcon,
  Mail,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  ShieldAlert,
  User as UserIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import { PixelDigits } from '@/components/brand/PixelDigits'
import { Wordmark } from '@/components/brand/Wordmark'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { useCurrentUser } from '@/features/auth'
import { useDmUnreadCount } from '@/features/messages'
import { unreadLabel, useUnreadCount } from '@/features/notifications'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/ui'

import { AccountMenu } from './AccountMenu'

interface NavItem {
  to: string
  label: MessageKey
  icon: LucideIcon
  end: boolean
  auth: boolean
  /** 显示哪种未读红点 */
  badge?: 'notifications' | 'messages'
  soon?: boolean
  /** 只给管理员看的入口 */
  admin?: boolean
}

const navItems: NavItem[] = [
  { to: '/', label: 'nav.home', icon: Home, end: true, auth: true },
  { to: '/explore', label: 'nav.explore', icon: Compass, end: false, auth: false },
  { to: '/search', label: 'nav.search', icon: Search, end: false, auth: false },
  {
    to: '/notifications',
    label: 'nav.notifications',
    icon: Bell,
    end: false,
    auth: true,
    badge: 'notifications',
  },
  { to: '/messages', label: 'nav.messages', icon: Mail, end: false, auth: true, badge: 'messages' },
  { to: '/bookmarks', label: 'nav.bookmarks', icon: Bookmark, end: false, auth: true },
  { to: '/lists', label: 'nav.lists', icon: ListIcon, end: false, auth: true },
  { to: '/settings', label: 'nav.settings', icon: Settings, end: false, auth: true },
  { to: '/admin', label: 'nav.admin', icon: ShieldAlert, end: false, auth: true, admin: true },
]

export function Sidebar() {
  const { user } = useCurrentUser()
  const openComposer = useUiStore((state) => state.openComposer)
  const unread = useUnreadCount(Boolean(user))
  const dmUnread = useDmUnreadCount(Boolean(user))
  const t = useT()

  const items = navItems.filter((item) => (!item.auth || user) && (!item.admin || user?.is_admin))
  const counts = {
    notifications: {
      count: unread.data?.count ?? 0,
      truncated: Boolean(unread.data?.truncated),
    },
    messages: { count: dmUnread.data?.count ?? 0, truncated: Boolean(dmUnread.data?.truncated) },
  }

  return (
    <nav
      className="sticky top-0 flex h-screen flex-col items-center gap-1 px-2 py-2 xl:items-stretch xl:px-3"
      aria-label={t('nav.main')}
    >
      <NavLink
        to="/"
        className="mb-1 self-center rounded-md p-3 hover:bg-surface-hover xl:self-start"
        aria-label={t('nav.homeLink')}
      >
        <Wordmark compact className="xl:hidden" />
        <Wordmark className="hidden xl:inline" />
      </NavLink>

      {items.map((item) => {
        const badge = item.badge ? counts[item.badge] : null
        const unreadCount = badge?.count ?? 0
        const unreadText = badge ? unreadLabel(badge.count, badge.truncated) : ''
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-4 rounded-md p-3 text-xl transition-colors',
                'hover:bg-surface-hover',
                isActive && 'font-bold',
              )
            }
            title={item.soon ? t('nav.soonTitle', { label: t(item.label) }) : t(item.label)}
          >
            <span className="relative shrink-0">
              <item.icon className="size-7" />
              {item.badge && unreadCount > 0 && (
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center px-corners [--px-step:1px] bg-brand px-[3px] text-on-brand"
                  aria-hidden
                >
                  <PixelDigits value={unreadText} />
                </span>
              )}
            </span>
            <span className="hidden xl:inline">
              {t(item.label)}
              {item.badge && unreadCount > 0 && (
                <span className="sr-only">{t('nav.unreadSuffix', { count: unreadText })}</span>
              )}
            </span>
            {item.soon && (
              <span className="ml-auto hidden rounded bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-faint xl:inline">
                {t('nav.comingSoon')}
              </span>
            )}
          </NavLink>
        )
      })}

      {/* /doc 由后端渲染，不是前端路由：用 <a> 整页跳转 */}
      <a
        href="/doc"
        className="group flex items-center gap-4 rounded-md p-3 text-xl transition-colors hover:bg-surface-hover"
        title={t('nav.agentDocsTitle')}
      >
        <BookOpen className="size-7 shrink-0" />
        <span className="hidden xl:inline">{t('nav.agentDocs')}</span>
        <span className="ml-auto hidden px-corners [--px-step:1px] bg-brand px-1.5 py-0.5 font-pixel text-[13px] leading-none font-bold text-on-brand xl:inline">
          /doc
        </span>
      </a>

      {user && (
        <>
          <NavLink
            to={`/${user.username}`}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-4 rounded-md p-3 text-xl transition-colors',
                'hover:bg-surface-hover',
                isActive && 'font-bold',
              )
            }
          >
            <UserIcon className="size-7 shrink-0" />
            <span className="hidden xl:inline">{t('nav.profile')}</span>
          </NavLink>

          <div className="mt-3 px-1">
            <Button size="lg" fullWidth onClick={() => openComposer()} className="hidden xl:flex">
              {t('common.post')}
            </Button>
            <button
              type="button"
              onClick={() => openComposer()}
              aria-label={t('common.post')}
              className="flex size-12 items-center justify-center px-corners bg-brand text-on-brand hover:bg-brand-hover xl:hidden"
            >
              <Plus className="size-6" />
            </button>
          </div>

          <div className="mt-auto pb-2">
            <AccountMenu
              align="left"
              trigger={({ toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-label={t('account.menuLabel', { username: user.username })}
                  className="flex w-full items-center gap-3 rounded-md p-2 hover:bg-surface-hover"
                >
                  <Avatar src={user.avatar_url} alt={user.display_name} size="sm" />
                  <span className="hidden min-w-0 flex-1 text-left xl:block">
                    <span className="block truncate text-[15px] font-bold">
                      {user.display_name}
                    </span>
                    <span className="block truncate text-[15px] text-ink-muted">
                      @{user.username}
                    </span>
                  </span>
                  <MoreHorizontal className="hidden size-5 xl:block" />
                </button>
              )}
            />
          </div>
        </>
      )}

      {!user && (
        <div className="mt-3 hidden xl:block">
          <NavLink to="/login">
            <Button size="lg" fullWidth>
              {t('common.login')}
            </Button>
          </NavLink>
        </div>
      )}
    </nav>
  )
}
